import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useResizable } from 'react-resizable-layout';
import { DynamicIcon, EntityOpenModeSwitcher, Tooltip } from 'src/components';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';
import { BlockContainer } from 'src/components/Layout/named-layout-block.view';
import { ResizeHandle } from 'src/components/ResizeHandle';
import { RecordOpenMode } from 'src/graphql';
import {
  RIGHT_PANEL_MAX_WIDTH_PX,
  RIGHT_PANEL_MIN_WIDTH_PX,
  RIGHT_PANEL_WIDTH_PX,
} from 'src/pages/Conversations/conversation';
import {
  PeekColumn,
  PeekGlassHeader,
  PeekGlassHeaderTitle,
  PeekPanelRoot,
  PeekToolsSlot,
} from './entity-peek.elements';
import { RIGHT_PANEL_RESIZE_GUTTER_WIDTH } from './entity-side-panel-layout';
import { useGlassHeaderTitleReveal } from './use-glass-header-title-reveal';

// keeps the grid from being squeezed to nothing while the panel is being resized
const MIN_GRID_VISIBLE_WIDTH_PX = 400;

const sidePanelWidthBoxSx: SxProps<Theme> = {
  flexShrink: 0,
  height: '100%',
  margin: '0 0.25rem 0 0',
};

const getGlassHeaderIconChipSx =
  (entityColor: string | ((theme: Theme) => string)): SxProps<Theme> =>
  (theme) => ({
    flexShrink: 0,
    width: '1.625rem',
    height: '1.625rem',
    borderRadius: '0.4375rem',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: typeof entityColor === 'function' ? entityColor(theme) : entityColor,
  });

interface EntityDetachedPanelProps {
  icon: string;
  entityColor: string | ((theme: Theme) => string);
  title: string | null | undefined;
  detailsCardTestId: string;
  resetKey: string | number;
  closeTooltip: string;
  menu?: ReactNode;
  onOpenFullPage?: () => void;
  onClose: () => void;
  children: (toolsSlot: HTMLElement | null) => ReactNode;
}

export const EntityDetachedPanel = ({
  icon,
  entityColor,
  title,
  detailsCardTestId,
  resetKey,
  closeTooltip,
  menu,
  onOpenFullPage,
  onClose,
  children,
}: EntityDetachedPanelProps) => {
  const panelViewRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [panelToolsSlot, setPanelToolsSlot] = useState<HTMLElement | null>(null);

  // the panel is the fixed-width side of the split (see records-grid-layout.tsx's
  // panelOwnsWidth mode) — the max has to stay below the viewport width too, or a wide
  // drag on a narrow window pushes the panel's own right edge past the screen
  const [maxPanelWidth, setMaxPanelWidth] = useState(() =>
    Math.min(RIGHT_PANEL_MAX_WIDTH_PX, window.innerWidth - MIN_GRID_VISIBLE_WIDTH_PX),
  );

  useEffect(() => {
    const updateMaxWidth = () =>
      setMaxPanelWidth(
        Math.min(RIGHT_PANEL_MAX_WIDTH_PX, window.innerWidth - MIN_GRID_VISIBLE_WIDTH_PX),
      );

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: Math.max(maxPanelWidth, RIGHT_PANEL_MIN_WIDTH_PX),
    containerRef: panelViewRef as RefObject<HTMLElement>,
    reverse: true,
  });

  const isTitleRevealed = useGlassHeaderTitleReveal({
    scrollerRef,
    headerRef,
    detailsCardTestId,
    resetKey,
  });

  return (
    <PeekPanelRoot ref={panelViewRef} direction="row" isDragging={isDragging}>
      {/* The card carries the only frame, so keep the handle out of the header
          band so it doesn't cut across the glass bar. */}
      <Box
        sx={{ position: 'relative', flexShrink: 0, width: RIGHT_PANEL_RESIZE_GUTTER_WIDTH }}
      >
        <ResizeHandle
          axis="x"
          isDragging={isDragging}
          {...separatorProps}
          sx={{ left: 0, right: 0, top: GLOBAL_TABS_HEIGHT }}
        />
      </Box>

      <Box
        sx={{
          ...sidePanelWidthBoxSx,
          width: Math.max(sidePanelWidth, 0),
          ...(isDragging && { transition: 'none' }),
        }}
      >
        <BlockContainer framed sx={{ height: '100%', position: 'relative' }}>
          <PeekGlassHeader ref={headerRef}>
            <PeekGlassHeaderTitle revealed={isTitleRevealed}>
              <Stack sx={getGlassHeaderIconChipSx(entityColor)}>
                <DynamicIcon
                  name={icon}
                  library="lucide"
                  sx={{ width: '0.875rem', height: '0.875rem', fontSize: '0.875rem' }}
                />
              </Stack>
              <Tooltip title={title}>
                <Typography variant="subtitle2" noWrap>
                  {title}
                </Typography>
              </Tooltip>
            </PeekGlassHeaderTitle>
            <Box flex="1" />
            {onOpenFullPage ? (
              <EntityOpenModeSwitcher
                openMode={RecordOpenMode.FullPage}
                onClick={onOpenFullPage}
              />
            ) : null}
            {menu}
            <Tooltip title={closeTooltip}>
              <DynamicIcon
                name="right_panel_close"
                sx={{ cursor: 'pointer' }}
                onClick={onClose}
              />
            </Tooltip>
          </PeekGlassHeader>

          <PeekColumn ref={scrollerRef}>
            <PeekToolsSlot ref={setPanelToolsSlot} />
            <Box
              sx={{
                pt: `calc(${GLOBAL_TABS_HEIGHT} + 0.5rem)`,
                pr: '0.25rem',
                pb: '0.5rem',
              }}
            >
              {children(panelToolsSlot)}
            </Box>
          </PeekColumn>
        </BlockContainer>
      </Box>
    </PeekPanelRoot>
  );
};
